import moment from 'moment-timezone'
import React, { MouseEvent } from 'react'
import { useMemo } from 'react'
import { CellProps, ColumnInstance } from 'react-table'
import { Box, Grid, IconButton, Link, makeStyles, Tooltip } from '@material-ui/core'
import { Edit as EditIcon, FileCopyOutlined as CopyIcon } from '@material-ui/icons'

import { filterByRequireRules } from 'civic-champs-shared/utils/filterByRequireRules'
import { NullableTextCell } from 'core/table/cells'
import { DATE_RANGE, DYNAMIC_TEXT } from 'core/table/filters'
import { STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'
import useOrgDateTimeCell from 'core/table/cells/useOrgDateTimeCell'
import { createDateValueSort } from 'components/table/sort'
import { useUISettings } from 'hooks/useUISettings'
import { useGetSuperEventLink, SuperEvent } from 'super-events/types'
import { isUndefined } from 'lodash'

export const useColumnStyles = makeStyles(theme => ({
  selectColumn: {
    '&>div': {
      width: '46px',
    },
    datetimeColumn: {
      '&>div': {
        width: '140px',
      },
    },
    editColumn: {
      '&>div': {
        width: '40px',
      },
    },
  },
}))

const useEditStyles = makeStyles({
  iconButton: {
    padding: 10,
  },
  iconHeader: {
    color: '#000',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
})

export const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'name':
      return STRING_OPERATOR_OPTIONS
    default:
      return []
  }
}

interface SuperEventRowActions {
  onEdit: (toEdit: SuperEvent) => void
  onCopy: (toCopy: SuperEvent) => void
}

export const useSuperEventColumns = (actions: SuperEventRowActions) => {
  const getSuperEventLink = useGetSuperEventLink()

  // @ts-ignore
  const { showAdminItems } = useUISettings()
  const { onEdit, onCopy } = actions

  const DateTimeCell = useOrgDateTimeCell()

  const colClasses = useColumnStyles()
  const editClasses = useEditStyles()

  return useMemo(() => {
    const SuperEventNameCell = (props: CellProps<SuperEvent>) => {
      const { row } = props

      return (
        <Link href={getSuperEventLink(row.original)} target="_blank">
          <NullableTextCell {...props} />
        </Link>
      )
    }

    const ActionsHeader = () => {
      return (
        <Grid container justify="space-between">
          <IconButton disabled className={editClasses.iconButton}>
            <EditIcon className={editClasses.iconHeader} fontSize="small" />
          </IconButton>
          <IconButton disabled className={editClasses.iconButton}>
            <CopyIcon className={editClasses.iconHeader} />
          </IconButton>
        </Grid>
      )
    }

    const ActionsCell = ({ cell }: CellProps<SuperEvent>) => {
      const superEvent = cell.row.original

      const handleEdit = (e: MouseEvent) => {
        e.stopPropagation()
        onEdit(superEvent)
      }

      const handleCopy = (e: MouseEvent) => {
        e.stopPropagation()
        onCopy(superEvent)
      }

      return (
        <Box component="span" className={editClasses.toolbar}>
          <Tooltip title="Edit">
            <IconButton onClick={handleEdit} size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Link">
            <IconButton onClick={handleCopy} size="small">
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }

    const now = moment()

    return filterByRequireRules(
      [
        {
          id: 'id',
          Header: 'ID',
          disableFilters: true,
          className: '',
          requires: 'showAdminItems',
        },
        {
          id: 'name',
          Header: 'Name',
          accessor: 'title', //TODO rename this property to name?
          Cell: SuperEventNameCell,
          filter: DYNAMIC_TEXT,
        },
        {
          id: 'startsAt',
          Header: 'Start',
          accessor: 'startsAt',
          Cell: DateTimeCell,
          filter: DATE_RANGE,
          sortType: createDateValueSort('startsAt'),
          className: colClasses.dateTimeColumn,
        },
        {
          id: 'endsAt',
          Header: 'End',
          accessor: 'endsAt',
          Cell: DateTimeCell,
          filter: DATE_RANGE,
          sortType: createDateValueSort('endsAt'),
          className: colClasses.dateTimeColumn,
        },
        {
          id: 'status',
          Header: 'Status',
          accessor: (superEvent: SuperEvent) => {
            const { startsAt, endsAt } = superEvent

            if (isUndefined(startsAt) || isUndefined(endsAt)) return null

            const now = moment()
            if (now.isBefore(startsAt)) return 'Upcoming'
            else if (now.isSameOrBefore(endsAt)) return 'Live'
            else return 'Concluded'
          },
          Cell: NullableTextCell,
        },
        {
          id: 'actions',
          //Header: <ActionsHeader />,
          Cell: ActionsCell,
          className: colClasses.editColumn,
        },
      ] as any,
      { showAdminItems },
    ) as any[]
  }, [
    DateTimeCell,
    colClasses.dateTimeColumn,
    colClasses.editColumn,
    showAdminItems,
    getSuperEventLink,
    editClasses.iconButton,
    editClasses.iconHeader,
    editClasses.toolbar,
    onEdit,
    onCopy,
  ])
}
